import React, { useState, useEffect, useRef, useCallback } from "react";

export interface Option {
  id: string;
  name: string;
}

interface AutocompleteProps {
  options: Option[];
  label: string;
  onSelect: (selectedOption: Option) => void;
  value: string[];
  searchValue: string;
  setSearchValue: (value: string) => void;
  isMultiple?: boolean;
  isLoading?: boolean;
}

export const Autocomplete: React.FC<AutocompleteProps> = ({
  options,
  onSelect,
  label,
  value,
  searchValue,
  setSearchValue,
  isMultiple = false,
  isLoading = false,
}) => {
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [firstRender, setFirstRender] = useState(true);
  const [tempSearchValue, setTempSearchValue] = useState<string>(searchValue);

  // Update filtered options when options change or when searching
  useEffect(() => {
    if (options.length > 0) {
      if (
        isDropdownOpen &&
        tempSearchValue &&
        tempSearchValue !== searchValue
      ) {
        // Filter options based on temporary search value when dropdown is open and user is typing
        setFilteredOptions(
          options.filter((option) =>
            option.name.toLowerCase().includes(tempSearchValue.toLowerCase())
          )
        );
      } else {
        // Show all options when dropdown is initially opened
        setFilteredOptions(options);
      }

      if (firstRender) {
        setFirstRender(false);
      }
    }
  }, [options, tempSearchValue, isDropdownOpen, firstRender, searchValue]);

  // Get selected options based on value array
  const selectedOptions = options.filter((option) => value.includes(option.id));

  const handleInputFocus = () => {
    setIsInputFocused(true);
    setIsDropdownOpen(true);
    // When focusing, show all options
    setFilteredOptions(options);
    setTempSearchValue(searchValue);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);

    // Only reset tempSearchValue if there's nothing selected and we're not in multi-select mode
    if (!selectedOptions.length && !isMultiple) {
      setTempSearchValue("");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTempSearchValue(value);
    setSearchValue(value);
    setFilteredOptions(
      options.filter((option) =>
        option.name.toLowerCase().includes(value.toLowerCase())
      )
    );
    setIsDropdownOpen(true);
  };

  const handleOptionClick = useCallback(
    (option: Option) => {
      if (isMultiple) {
        onSelect(option);
        let newValues = selectedOptions.map((opt) => opt.name);
        if (value.includes(option.id)) {
          newValues = selectedOptions
            .filter((opt) => opt.id !== option.id)
            .map((opt) => opt.name);
        } else {
          newValues.push(option.name);
        }
        console.log(newValues.join(", "));
        setSearchValue(newValues.join(", "));
        setTempSearchValue(newValues.join(", "));
      } else {
        // For single select, close dropdown after selection
        onSelect(option);
        setIsDropdownOpen(false);
        setSearchValue(option.name);
        setTempSearchValue(option.name);
      }
    },
    [isMultiple, onSelect, selectedOptions, setSearchValue, value]
  );

  // When dropdown toggle is clicked, show all options
  const handleDropdownToggle = () => {
    const newState = !isDropdownOpen;
    setIsDropdownOpen(newState);
    if (newState) {
      setFilteredOptions(options);
    }
  };

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div id="hs-combobox-basic-usage" className="relative" data-hs-combo-box="">
      <label className={"text-sm font-medium text-navy-700 dark:text-white"}>
        {label}
      </label>
      <div className="relative">
        <input
          className="dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-400 dark:placeholder-neutral-500 dark:focus:ring-neutral-600 mt-2 block w-full rounded-lg border border-gray-200 py-3 pe-9 ps-4 text-sm focus:border-blue-500 focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
          type="text"
          value={isDropdownOpen ? tempSearchValue : searchValue}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          placeholder={isInputFocused ? "Rechercher..." : ""}
          data-hs-combo-box-input=""
        />
        <div
          className="absolute end-3 top-1/2 -translate-y-1/2"
          aria-expanded="false"
          data-hs-combo-box-toggle=""
          onClick={handleDropdownToggle}
        >
          {isLoading ? (
            <svg
              className="h-3.5 w-3.5 animate-spin text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            <svg
              className="dark:text-neutral-500 size-3.5 shrink-0 text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m7 15 5 5 5-5"></path>
              <path d="m7 9 5-5 5 5"></path>
            </svg>
          )}
        </div>
      </div>
      {isDropdownOpen && (
        <div
          ref={dropdownRef}
          className="dark:[&::-webkit-scrollbar-track]:bg-neutral-700 dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500 dark:bg-neutral-900 dark:border-neutral-700 absolute z-50 max-h-72 w-full overflow-hidden overflow-y-auto rounded-lg border border-gray-200 bg-white p-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar]:w-2"
          data-hs-combo-box-output=""
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <svg
                className="h-5 w-5 animate-spin text-gray-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="ml-2 text-sm text-gray-500">Chargement...</span>
            </div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.id}
                className={`dark:bg-neutral-900 dark:hover:bg-neutral-800 dark:text-neutral-200 dark:focus:bg-neutral-800 w-full cursor-pointer rounded-lg px-4 py-2 text-sm text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                  value.includes(option.id)
                    ? "bg-neutral-800 dark:bg-neutral-700"
                    : ""
                }`}
                tabIndex={index}
                onClick={() => handleOptionClick(option)}
              >
                <div className="flex w-full items-center justify-between">
                  <span>{option.name}</span>
                  {value.includes(option.id) && (
                    <span>
                      <svg
                        className="size-3.5 shrink-0 text-blue-600 dark:text-blue-500"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M20 6 9 17l-5-5"></path>
                      </svg>
                    </span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="px-4 py-2 text-sm text-gray-500">
              Aucun résultat
            </div>
          )}
        </div>
      )}
    </div>
  );
};
